import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, SectionHeader } from "@pentabd/ui";

import { CancelButton, ErrorModal, SuccessModal } from "@components";
import { BasicInfo, JobInfo, JoiningInfo } from "./components";

import { ROUTES } from "@constants/routes";

import { useGeneralInfo } from "@hooks/HRM/employee/useGeneralInfo";

import { GeneralInfoType } from "@type/HRM/general-info";

import { generalInfoValidation } from "@validations/HRM/employee/generalInfoValidation";
import { TABS } from "@constants/tab-serial";

const {
  HRM: { EMPLOYEES },
} = ROUTES;

export interface GeneralInfoPropsType {
  id: number | null; //This "id" is important. when we creating a new Employee it will be null. But when we updating, a id will must be there.
}

function GeneralInfo({ id }: GeneralInfoPropsType) {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [createErrorMessage, setCreateErrorMessage] = useState("");

  const navigate = useNavigate();

  const { t } = useTranslation();

  const closeErrorModal = () => setIsErrorModalOpen(false);

  const {
    generalInfo,
    addGeneralInfo,
    changeGeneralInfo,
    getGeneralInfoRequestOngoing,
    createGeneralInfoRequestOngoing,
    createGeneralInfoSuccess,
    createGeneralInfoFailed,
    createGeneralInfoError,
    updateGeneralInfoRequestOngoing,
    updateGeneralInfoSuccess,
    updateGeneralInfoFailed,
  } = useGeneralInfo(id);

  const methods = useForm<GeneralInfoType>({
    resolver: yupResolver(generalInfoValidation),
  });

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<GeneralInfoType> = (data: any) => {
    const newEmployee = {
      ...data,
      onProbation: data?.onProbation === "yes" ? true : false,
    };
    if (id) changeGeneralInfo(newEmployee);
    else addGeneralInfo(newEmployee);
  };

  const createSuccessAction = () => {
    if ("id" in generalInfo)
      navigate(`${EMPLOYEES}/${generalInfo.id}?tab=${TABS.ATTACHED_FILES}`);
  };

  let isLoading =
    getGeneralInfoRequestOngoing ||
    createGeneralInfoRequestOngoing ||
    updateGeneralInfoRequestOngoing;

  useEffect(() => {
    if (createGeneralInfoSuccess || updateGeneralInfoSuccess)
      setIsSuccessModalOpen(true);
    else if (createGeneralInfoFailed || updateGeneralInfoFailed)
      setIsErrorModalOpen(true);
    if (
      createGeneralInfoError?.response &&
      createGeneralInfoError?.response?.data
    ) {
      setCreateErrorMessage(
        createGeneralInfoError?.response?.data?.message ||
          "something was wrong",
      );
    }
  }, [
    createGeneralInfoSuccess,
    createGeneralInfoFailed,
    updateGeneralInfoSuccess,
    updateGeneralInfoFailed,
    createGeneralInfoError,
  ]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setCreateErrorMessage("");
    };
    return reset();
  }, []);

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle="You have created a new Employee"
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={createErrorMessage}
        />
      </>
      {/* MODAL SECTION */}

      {/* Whole general info form is here */}
      <FormProvider {...methods}>
        <form
          className="container position-relative"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* FORM HEADING SECTION */}
          {/* We keep it in this form section because the "Next" button will submit the form . So Next button is a part of this form*/}

          <div className="pt-10">
            <SectionHeader
              title={t("EMPLOYEE.GENERAL_INFO.HEADING")}
              subtitle={t("EMPLOYEE.GENERAL_INFO.DESCRIPTION")}
              components={[
                <Button
                  key="general_info_next_top"
                  htmlType="submit"
                  type="primary"
                  loading={isLoading}
                >
                  {t("EMPLOYEE.GENERAL_INFO.NEXT_BUTTON")}
                </Button>,
              ]}
            />
          </div>
          {/* FORM HEADING SECTION */}

          {/* We divide the whole form into 3 parts */}
          <div className="mt-10">
            <BasicInfo />
            <JobInfo />
            <JoiningInfo />

            {/* This is the regular submit button */}
            <div className="row py-10">
              <div className="d-flex justify-content-end gap-8 col-lg-10">
                <CancelButton />
                <Button
                  key="general_info_save"
                  htmlType="submit"
                  fill="outline"
                  type="primary"
                  size="sm"
                  loading={isLoading}
                >
                  {t("EMPLOYEE.GENERAL_INFO.FORM.SAVE_BUTTON")}
                </Button>
              </div>
            </div>
          </div>

          {/* This "Next" button will be submit this form. So it is also a part of this form */}
          <div className="d-flex flex-row-reverse border-top py-10">
            <Button
              key="general_info_next_bottom"
              htmlType="submit"
              type="info"
              loading={isLoading}
            >
              {t("EMPLOYEE.GENERAL_INFO.NEXT_BUTTON")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
}

export default GeneralInfo;
